import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLNAuth } from '@shared/context/LNAuthContext';
import MobilePage from '../components/MobilePage';
import {
  getRecentlyPlayed,
  getTopTracks,
  getTopArtists,
} from '@shared/services/api';

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

const TrackItem = ({ track, index, showRank = false, showTime = false, playedAt }) => (
  <div className="flex items-center gap-3 py-2 hover:bg-white/5 rounded-xl px-2 transition-all">
    {showRank && (
      <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center flex-shrink-0">
        <span className="text-[10px] font-bold text-white">{index + 1}</span>
      </div>
    )}
    <img
      src={track.album?.images?.[2]?.url || track.album?.images?.[0]?.url}
      alt=""
      className="w-10 h-10 rounded-lg bg-white/10 flex-shrink-0"
      loading="lazy"
    />
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm truncate">{track.name}</p>
      <p className="text-white/40 text-xs truncate">{track.artists?.[0]?.name}</p>
    </div>
    {showTime && playedAt && (
      <span className="text-white/20 text-[10px] flex-shrink-0">{getTimeAgo(new Date(playedAt))}</span>
    )}
  </div>
);

const ArtistItem = ({ artist, index, showRank = false }) => (
  <div className="flex items-center gap-3 py-2 hover:bg-white/5 rounded-xl px-2 transition-all">
    {showRank && (
      <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center flex-shrink-0">
        <span className="text-[10px] font-bold text-white">{index + 1}</span>
      </div>
    )}
    <img
      src={artist.images?.[2]?.url || artist.images?.[0]?.url}
      alt=""
      className="w-10 h-10 rounded-full bg-white/10 flex-shrink-0 ring-1 ring-white/10"
      loading="lazy"
    />
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm truncate">{artist.name}</p>
      <p className="text-white/40 text-xs truncate">
        {artist.followers?.total?.toLocaleString()} followers
      </p>
    </div>
  </div>
);

const ScrollCard = ({ imageUrl, title, subtitle, rank, badge, round = false, to }) => {
  const content = (
    <div className="flex-shrink-0 w-32 md:w-36">
      <div className="relative">
        <img
          src={imageUrl}
          alt=""
          className={`w-32 h-32 md:w-36 md:h-36 object-cover bg-white/5 ${round ? 'rounded-full' : 'rounded-xl'}`}
          loading="lazy"
        />
        {rank && (
          <div className="absolute bottom-1.5 left-1.5 w-6 h-6 rounded bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">{rank}</span>
          </div>
        )}
        {badge && (
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm">
            <span className="text-[9px] font-medium text-white/70">{badge}</span>
          </div>
        )}
      </div>
      <p className="text-white font-medium text-xs mt-2 truncate">{title}</p>
      <p className="text-white/40 text-[10px] truncate">{subtitle}</p>
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  return content;
};

const CardListItem = ({ imageUrl, title, subtitle, rank, badge, round = false, to }) => {
  const content = (
    <div className="flex items-center gap-3 py-1.5 px-2 rounded-xl hover:bg-white/5 transition-all">
      <div className="relative flex-shrink-0">
        <img
          src={imageUrl}
          alt=""
          className={`w-14 h-14 object-cover bg-white/5 ${round ? 'rounded-full' : 'rounded-lg'}`}
          loading="lazy"
        />
        {rank && (
          <div className="absolute -bottom-1 -left-1 w-5 h-5 rounded bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <span className="text-[9px] font-bold text-white">{rank}</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm truncate">{title}</p>
        <p className="text-white/40 text-xs truncate">{subtitle}</p>
      </div>
      {badge && (
        <span className="text-white/20 text-[10px] flex-shrink-0">{badge}</span>
      )}
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  return content;
};

const SectionHeader = ({ title, onSeeAll }) => (
  <div className="flex items-center justify-between mb-2">
    <h2 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h2>
    <button onClick={onSeeAll} className="text-white/40 text-xs font-medium hover:text-white transition-colors">
      See all
    </button>
  </div>
);

const SectionSkeleton = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="flex items-center gap-3 py-2 px-2">
        <div className="w-10 h-10 rounded-lg bg-white/5 animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-white/5 rounded animate-pulse w-3/4" />
          <div className="h-2.5 bg-white/5 rounded animate-pulse w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Full list view (shown when "See all" is tapped)
// ---------------------------------------------------------------------------

const Tab = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`pb-2 text-sm font-bold whitespace-nowrap transition-all touch-manipulation ${
      active ? 'text-white border-b-2 border-white' : 'text-white/40 hover:text-white/60'
    }`}
  >
    {children}
  </button>
);

const TimeRangePill = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-all touch-manipulation ${
      active ? 'bg-white/20 text-white' : 'text-white/30 active:text-white/60'
    }`}
  >
    {children}
  </button>
);

export function FullListView({ initialTab, initialTimeRange = 'short_term', onBack }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setItems([]);
      try {
        switch (activeTab) {
          case 'recent':
            const recent = await getRecentlyPlayed(50);
            setItems(recent.items || []);
            break;
          case 'tracks':
            const tracks = await getTopTracks(timeRange, 50);
            setItems(tracks.items || []);
            break;
          case 'artists':
            const artists = await getTopArtists(timeRange, 50);
            setItems(artists.items || []);
            break;
        }
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTab, timeRange]);

  return (
    <>
      {/* Back + tabs + time range — sticky */}
      <div className="sticky top-0 z-10 -mx-4 px-4 lg:-mx-8 lg:px-8 pt-2 pb-3" style={{ backgroundColor: 'var(--ln-bg, #000)' }}>
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-white/40 hover:text-white transition-colors flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex gap-6 overflow-x-auto scrollbar-hide">
            <Tab active={activeTab === 'recent'} onClick={() => setActiveTab('recent')}>Recent</Tab>
            <Tab active={activeTab === 'tracks'} onClick={() => setActiveTab('tracks')}>Top Tracks</Tab>
            <Tab active={activeTab === 'artists'} onClick={() => setActiveTab('artists')}>Top Artists</Tab>
          </div>
        </div>
        {(activeTab === 'tracks' || activeTab === 'artists') && (
          <div className="flex gap-2 mt-2">
            <TimeRangePill active={timeRange === 'short_term'} onClick={() => setTimeRange('short_term')}>4 weeks</TimeRangePill>
            <TimeRangePill active={timeRange === 'medium_term'} onClick={() => setTimeRange('medium_term')}>6 months</TimeRangePill>
            <TimeRangePill active={timeRange === 'long_term'} onClick={() => setTimeRange('long_term')}>All time</TimeRangePill>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <SectionSkeleton />
      ) : items.length === 0 ? (
        <p className="text-white/20 text-xs py-8 text-center">No data yet — listen to more music!</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4">
          {activeTab === 'recent' && items.map((item, i) => (
            item?.track ? <TrackItem key={`${item.track.id}-${item.played_at}`} track={item.track} index={i} showTime playedAt={item.played_at} /> : null
          ))}
          {activeTab === 'tracks' && items.map((track, i) => (
            <TrackItem key={track.id} track={track} index={i} showRank />
          ))}
          {activeTab === 'artists' && items.map((artist, i) => (
            <ArtistItem key={artist.id} artist={artist} index={i} showRank />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dashboard (overview with quadrants)
// ---------------------------------------------------------------------------

function DashboardPage() {
  const { user } = useLNAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ recent: [], topTracks: [], topArtists: [], tracksTimeRange: 'short_term', artistsTimeRange: 'short_term' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [recent, tracks, artists] = await Promise.allSettled([
          getRecentlyPlayed(50),
          getTopTracks('short_term', 50, { fallback: true }),
          getTopArtists('short_term', 50, { fallback: true }),
        ]);
        setData({
          recent: recent.status === 'fulfilled' ? (recent.value.items || []) : [],
          topTracks: tracks.status === 'fulfilled' ? (tracks.value.items || []) : [],
          topArtists: artists.status === 'fulfilled' ? (artists.value.items || []) : [],
          tracksTimeRange: tracks.status === 'fulfilled' ? (tracks.value.time_range_used || 'short_term') : 'short_term',
          artistsTimeRange: artists.status === 'fulfilled' ? (artists.value.time_range_used || 'short_term') : 'short_term',
        });
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  return (
    <MobilePage>
        {loading ? (
          <div className="mt-2">
            {/* Featured cards skeleton */}
            <div className="flex gap-4 overflow-hidden pb-2 -mx-1 px-1 mb-8">
              <div className="flex-shrink-0 w-[280px] md:w-[calc(50%-8px)] h-[160px] md:h-[180px] rounded-2xl bg-white/5 animate-pulse" />
              <div className="flex-shrink-0 w-[280px] md:w-[calc(50%-8px)] h-[160px] md:h-[180px] rounded-2xl bg-white/5 animate-pulse" />
            </div>

            {/* Top Artists horizontal scroll skeleton */}
            <div className="mb-8">
              <div className="h-4 bg-white/5 rounded w-28 mb-3 animate-pulse" />
              <div className="flex gap-3 overflow-hidden pb-2 -mx-1 px-1">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-32 md:w-36">
                    <div className="w-32 h-32 md:w-36 md:h-36 rounded-full bg-white/5 animate-pulse" />
                    <div className="mt-2 h-3 bg-white/5 rounded w-20 mx-auto animate-pulse" />
                  </div>
                ))}
              </div>
            </div>

            {/* Two-column list skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="h-4 bg-white/5 rounded w-32 mb-3 animate-pulse" />
                <SectionSkeleton />
              </div>
              <div>
                <div className="h-4 bg-white/5 rounded w-24 mb-3 animate-pulse" />
                <SectionSkeleton />
              </div>
            </div>
          </div>
        ) : (
          <>
          {/* Featured */}
          <div className="mt-2 mb-8">
            <div className="flex md:flex-wrap gap-4 overflow-x-auto md:overflow-visible scrollbar-hide pb-2 -mx-1 px-1 snap-x snap-mandatory md:[&>a]:w-[calc(50%-8px)] md:[&>div]:w-[calc(50%-8px)]">
              <Link to="/festival" className="flex-shrink-0 md:flex-shrink snap-start w-[280px] md:w-auto h-[160px] md:h-[180px] rounded-2xl overflow-hidden relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4 text-white/40">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"/></svg>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-black text-lg leading-tight">Festival<br/>Planner</p>
                  <p className="text-white/60 text-xs mt-1">Build your schedule based on your taste</p>
                </div>
              </Link>

              <div className="flex-shrink-0 md:flex-shrink snap-start w-[280px] md:w-auto h-[160px] md:h-[180px] rounded-2xl overflow-hidden relative opacity-60">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-400" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4 text-white/40">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/></svg>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white font-black text-lg leading-tight">Concert<br/>Recommendations</p>
                  <p className="text-white/40 text-xs mt-1">Coming soon</p>
                </div>
              </div>

            </div>
          </div>

          {/* Top Artists — horizontal scroll cards */}
          <div className="mt-2 mb-8">
            <SectionHeader title="Top Artists" onSeeAll={() => navigate('/dashboard/all/top-artists')} />
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1">
              {data.topArtists.slice(0, 20).map((artist, i) => (
                <ScrollCard key={`scroll-${artist.id}`}
                  imageUrl={artist.images?.[1]?.url || artist.images?.[0]?.url}
                  title={artist.name}
                  subtitle={`${artist.followers?.total?.toLocaleString()} followers`}
                  rank={i + 1}
                  round={true}
                />
              ))}
              {data.topArtists.length === 0 && <p className="text-white/20 text-xs py-4">No data yet — listen to more music!</p>}
            </div>
          </div>

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-2 md:gap-8">
            {/* Recently Played */}
            <div>
              <SectionHeader title="Recently Played" onSeeAll={() => navigate('/dashboard/all/recently-played')} />
              <div className="md:max-h-[400px] md:overflow-y-auto scrollbar-hide">
                {/* Mobile: cards (5 items, no scroll), Desktop: original list */}
                <div className="md:hidden space-y-1">
                  {data.recent.slice(0, 5).map((item, i) => (
                    <CardListItem key={`${item.track.id}-${item.played_at}`}
                      imageUrl={item.track.album?.images?.[1]?.url || item.track.album?.images?.[0]?.url}
                      title={item.track.name}
                      subtitle={item.track.artists?.[0]?.name}
                      badge={getTimeAgo(new Date(item.played_at))}
                      round={false}
                    />
                  ))}
                </div>
                <div className="hidden md:grid grid-cols-1 xl:grid-cols-2 gap-x-4">
                  {data.recent.slice(0, 50).map((item, i) => (
                    <TrackItem key={`${item.track.id}-${item.played_at}`} track={item.track} index={i} showTime playedAt={item.played_at} />
                  ))}
                </div>
              </div>
              {data.recent.length === 0 && <p className="text-white/20 text-xs py-4">No recent plays</p>}
            </div>

            {/* Top Tracks */}
            <div>
              <SectionHeader title="Top Tracks" onSeeAll={() => navigate('/dashboard/all/top-tracks')} />
              <div className="md:max-h-[400px] md:overflow-y-auto scrollbar-hide">
                <div className="md:hidden space-y-1">
                  {data.topTracks.slice(0, 5).map((track, i) => (
                    <CardListItem key={track.id}
                      imageUrl={track.album?.images?.[1]?.url || track.album?.images?.[0]?.url}
                      title={track.name}
                      subtitle={track.artists?.[0]?.name}
                      rank={i + 1}
                      round={false}
                    />
                  ))}
                </div>
                <div className="hidden md:grid grid-cols-1 xl:grid-cols-2 gap-x-4">
                  {data.topTracks.slice(0, 50).map((track, i) => (
                    <TrackItem key={track.id} track={track} index={i} showRank />
                  ))}
                </div>
              </div>
              {data.topTracks.length === 0 && <p className="text-white/20 text-xs py-4">No data yet — listen to more music!</p>}
            </div>
          </div>
          </>
        )}
    </MobilePage>
  );
}

export default DashboardPage;
